import { useRef, useState } from "react";
import { useAuthStore, type TenderBadgePreference } from "@/store/auth";
import { useUIStore } from "@/store/ui";
import { useUpdateProfile, useUploadAvatar, useDeleteAvatar, useUpdateTenderBadgePreferences } from "../hooks/useSettings";
import { AvatarCropModal } from "../components/AvatarCropModal";
import { ChangePasswordModal } from "../components/ChangePasswordModal";
import { NotificationDelegationSection } from "../components/NotificationDelegationSection";
import { MyPermissionsSection } from "../components/MyPermissionsSection";
import { Camera, Trash2, Briefcase, Lock, AlertTriangle, KeyRound } from "lucide-react";
import { clsx } from "clsx";

const CONTRACT_TYPE_LABELS: Record<string, string> = {
  uop: "Umowa o pracę (UoP)",
  uz: "Umowa zlecenie (UZ)",
  b2b: "B2B / Kontrakt",
};

// Wymiar urlopu ŚWIADOMIE nie jest tu pobierany: sekcja „Zatrudnienie" jest zakomentowana
// (patrz niżej), a `/leave-entitlement/` jest zamknięte dla ludzi z zewnątrz — zapytanie
// puszczane mimo to sypało podwykonawcom 403 przy każdym wejściu w Ustawienia. Gdy sekcja
// wróci, zapytanie ma wrócić RAZEM z bramką na wewnętrznego użytkownika (`enabled`).
export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const sidebarSearchEnabled = useUIStore((s) => s.sidebarSearchEnabled);
  const setSidebarSearchEnabled = useUIStore((s) => s.setSidebarSearchEnabled);
  const { mutate: uploadAvatar, isPending: isUploading } = useUploadAvatar();
  const { mutate: updateProfile, isPending: isSaving } = useUpdateProfile();
  const { mutate: deleteAvatar, isPending: isDeleting } = useDeleteAvatar();
  const { mutate: updateBadgePrefs } = useUpdateTenderBadgePreferences();

  // Badge configuration — one toggle per tender action status, shown only for the
  // statuses the user is permitted to act on. Missing preference (older session) = on.
  const badgePref = user?.tender_badge_preference;
  const badgeOptions: { field: keyof TenderBadgePreference; label: string; desc: string; can: boolean }[] = [
    { field: "show_needs_pricing", label: "Wymaga wyceny", desc: "Przetargi czekające na wyliczenie wyceny.", can: !!user?.has_calculate_pricing_permission },
    { field: "show_needs_approval", label: "Oczekuje na zatwierdzenie wyceny", desc: "Wyceny czekające na Twoją akceptację.", can: !!user?.has_approve_pricing_permission },
    { field: "show_needs_offer", label: "Wymaga wystawienia oferty", desc: "Zaakceptowane wyceny czekające na złożenie oferty.", can: !!user?.has_issue_offers_permission },
    { field: "show_needs_result", label: "Oferta złożona — minęło 21 dni bez wyniku", desc: "Złożone oferty bez oznaczonego wyniku (wygrana/przegrana) po terminie.", can: !!user?.has_issue_offers_permission },
  ];
  const visibleBadgeOptions = badgeOptions.filter((o) => o.can);

  // Print-order notifications (DLC_02) — opt-in, only offered to the module's audience. Not to
  // coordinators: a print order has no owner to scope "theirs", so the flag would feed them other
  // employees' orders (backend ignores it for them — is_print_order_realization_recipient).
  const hasPaperSend =
    (user?.accessible_dlc_modules ?? []).includes("dlc_02") && user?.role !== "coordinator";
  const printOrderNotifications = !!user?.receives_print_order_notifications;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const parseLocalPhone = (full: string) => {
    const digits = full.replace(/\D/g, "");
    if (digits.startsWith("48") && digits.length > 9) return digits.slice(2);
    return digits.slice(-9);
  };
  const [contactPhone, setContactPhone] = useState(() => parseLocalPhone(user?.phone_number ?? ""));
  const [contactEmail, setContactEmail] = useState(user?.email ?? "");
  const [contactSaved, setContactSaved] = useState(false);

  const isInternal = user?.role !== "contractor";
  const contractType = user?.contract_type ?? (user?.is_uz ? "uz" : "uop");
  const contractExpiryInfo = user?.contract_expiry_info ?? [];
  const soonExpiring = contractExpiryInfo.filter((e) => e.days_to_expiry <= 45);

  const initials =
    `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase() || "?";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    e.target.value = "";
  };

  const handleCropSave = (blob: Blob) => {
    uploadAvatar(blob, {
      onSuccess: () => {
        URL.revokeObjectURL(cropSrc!);
        setCropSrc(null);
      },
    });
  };

  const handleCropClose = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleContactSave = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile({ phone_number: contactPhone ? `+48${contactPhone}` : "", email: contactEmail }, {
      onSuccess: () => {
        setContactSaved(true);
        setTimeout(() => setContactSaved(false), 2500);
      },
    });
  };

  const handleContactReset = () => {
    setContactPhone(parseLocalPhone(user?.phone_number ?? ""));
    setContactEmail(user?.email ?? "");
  };

  return (
    <>
      {cropSrc && (
        <AvatarCropModal
          imageSrc={cropSrc}
          onSave={handleCropSave}
          onClose={handleCropClose}
          isSaving={isUploading}
        />
      )}

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      <div className="p-6 max-w-2xl mx-auto space-y-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Ustawienia konta</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-5" />

        {/* Avatar */}
        <div className="bg-white dark:bg-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Zdjęcie profilowe</h2>
          <div className="flex items-center gap-5">
            <div className="relative flex-shrink-0">
              {user?.profile_picture_url ? (
                <img
                  src={user.profile_picture_url}
                  alt="Avatar"
                  className="w-20 h-20 object-cover border-2 border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-20 h-20 bg-[var(--color-primary)] flex items-center justify-center text-white text-2xl font-bold select-none">
                  {initials}
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || isDeleting}
                className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-full p-1.5 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                title="Zmień zdjęcie"
              >
                <Camera size={14} className="text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{user?.email}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">JPG, PNG lub WebP · maks. 5 MB</p>

              {(isUploading || isDeleting) && (
                <p className="text-xs text-[var(--color-primary)] mt-1.5">
                  {isDeleting ? "Usuwanie..." : "Zapisywanie..."}
                </p>
              )}

              {user?.profile_picture_url && !isDeleting && !isUploading && (
                <button
                  type="button"
                  onClick={() => deleteAvatar()}
                  className="mt-2 flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
                >
                  <Trash2 size={12} />
                  Usuń zdjęcie
                </button>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Contact info */}
        <div className="bg-white dark:bg-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Dane kontaktowe</h2>
          <form onSubmit={handleContactSave} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Telefon
              </label>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-[var(--color-primary)] focus-within:border-[var(--color-primary)] overflow-hidden bg-white dark:bg-gray-700">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-600 border-r border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-700 dark:text-gray-200 select-none whitespace-nowrap">
                  <span>🇵🇱</span>
                  <span>+48</span>
                </div>
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  placeholder="000 000 000"
                  maxLength={9}
                  className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                E-mail
              </label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="adres@example.com"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={handleContactReset}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white disabled:opacity-50"
              >
                {contactSaved ? "Zapisano" : isSaving ? "Zapisywanie…" : "Zapisz"}
              </button>
            </div>
          </form>
        </div>

        {/* Security — self-service password change */}
        <div className="bg-white dark:bg-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Bezpieczeństwo</h2>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Hasło</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Ustaw nowe hasło do swojego konta.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 whitespace-nowrap"
            >
              <KeyRound size={14} />
              Zmień hasło
            </button>
          </div>
        </div>

        {/* Uprawnienia z dodatkowych flag konta — także te z pełnionej funkcji. */}
        <MyPermissionsSection />

        {/* Przekazywanie powiadomień — internal users only: contractors have no branch to
            delegate within, and the endpoint answers them 403. */}
        {isInternal && <NotificationDelegationSection />}

        {/* Interface preferences (stored locally in the browser) */}
        <div className="bg-white dark:bg-gray-800 p-6">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Interfejs</h2>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Wyszukiwarka w menu bocznym
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Pole filtrowania pozycji menu, widoczne na dole panelu bocznego.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={sidebarSearchEnabled}
              onClick={() => setSidebarSearchEnabled(!sidebarSearchEnabled)}
              className={clsx(
                "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors",
                sidebarSearchEnabled
                  ? "bg-[var(--color-primary)]"
                  : "bg-gray-300 dark:bg-gray-600",
              )}
            >
              <span
                className={clsx(
                  "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                  sidebarSearchEnabled ? "translate-x-[18px]" : "translate-x-1",
                )}
              />
            </button>
          </div>
        </div>

        {/* Tender badge preferences (stored in DB; applies to Przetargi + Przetargi ZE nav) */}
        {visibleBadgeOptions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Badge przetargów</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Wybierz, które liczniki mają pokazywać się jako badge przy „Przetargi" i „Przetargi ZE" w menu.
            </p>
            <div className="space-y-4">
              {visibleBadgeOptions.map((opt) => {
                const checked = badgePref ? badgePref[opt.field] : true;
                return (
                  <div key={opt.field} className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{opt.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.desc}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={checked}
                      onClick={() => updateBadgePrefs({ [opt.field]: !checked })}
                      className={clsx(
                        "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors",
                        checked ? "bg-[var(--color-primary)]" : "bg-gray-300 dark:bg-gray-600",
                      )}
                    >
                      <span
                        className={clsx(
                          "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                          checked ? "translate-x-[18px]" : "translate-x-1",
                        )}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Print-order notifications (DLC_02) — opt-in, off for everyone by default */}
        {hasPaperSend && (
          <div className="bg-white dark:bg-gray-800 p-6">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Zlecenia wydruku</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Domyślnie wyłączone — włącz, jeśli zajmujesz się wydrukiem i wysyłką dokumentów.
            </p>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Powiadomienia o zleceniach do wydruku
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Powiadomienie i badge, gdy pojawi się zaakceptowane zlecenie wydruku (oraz wydruk
                  czekający na wysyłkę). Bez tego zlecenia nadal widzisz na liście.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={printOrderNotifications}
                aria-label="Powiadomienia o zleceniach do wydruku"
                disabled={isSaving}
                onClick={() =>
                  updateProfile({ receives_print_order_notifications: !printOrderNotifications })
                }
                className={clsx(
                  "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors",
                  printOrderNotifications ? "bg-[var(--color-primary)]" : "bg-gray-300 dark:bg-gray-600",
                )}
              >
                <span
                  className={clsx(
                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                    printOrderNotifications ? "translate-x-[18px]" : "translate-x-1",
                  )}
                />
              </button>
            </div>
          </div>
        )}

        {/* Employment info — hidden temporarily, uncomment to restore */}
        {/* {isInternal && (
          <>
            <div className="bg-white dark:bg-gray-800 p-6">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Zatrudnienie</h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Briefcase size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {CONTRACT_TYPE_LABELS[contractType] ?? contractType}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-sm">
                      {contractType === "uz"
                        ? "W harmonogramie domyślnie jesteś nieobecny — zaznacz dni, w których będziesz w biurze."
                        : contractType === "b2b"
                        ? "Kontrakt B2B."
                        : "W harmonogramie jesteś domyślnie obecny."}
                    </p>
                  </div>
                </div>
                <Lock size={13} className="text-gray-300 dark:text-gray-600 flex-shrink-0 ml-4" title="Zmiana możliwa tylko przez dyrektora" />
              </div>

              {soonExpiring.length > 0 && (
                <div className="mt-4 space-y-2">
                  {soonExpiring.map((exp, i) => (
                    <div
                      key={i}
                      className={clsx(
                        "flex items-start gap-2 rounded-md px-3 py-2 text-xs",
                        exp.days_to_expiry <= 14
                          ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                          : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                      )}
                    >
                      <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                      <span>
                        Umowa z <strong>{exp.company_name}</strong> kończy się{" "}
                        {new Date(exp.effective_to).toLocaleDateString("pl-PL")}{" "}
                        (za {exp.days_to_expiry} {exp.days_to_expiry === 1 ? "dzień" : "dni"})
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )} */}
      </div>
    </>
  );
}
